"use client";

import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import { useAuth } from "@/context/AuthContext";
import { useParams } from "@/hooks";
import type { ScheduleNotificationTemplate } from "@/types/schedules";
import {
  fetchNotificationsSchedule,
  createNotificationsSchedule,
  updateNotificationsSchedule,
  deleteNotificationsSchedule,
} from "@/store/modules/notificationsSchedule/thunks";

const useNotificationsSchedule = (onSuccess?: () => void) => {
  const dispatch: AppDispatch = useDispatch();
  const { establishment, baseURL } = useAuth();
  const { params } = useParams();

  const { data, loading, error } = useSelector(
    (state: any) =>
      state.notificationsSchedule || {
        data: null as ScheduleNotificationTemplate[] | null,
        loading: false,
        error: null,
      },
  );

  useEffect(() => {
    if (!establishment || !baseURL) return;
    dispatch(fetchNotificationsSchedule(params));
  }, [dispatch, establishment, baseURL, params]);

  const createTemplate = useCallback(
    async (payload: any) => {
      const result = await dispatch(createNotificationsSchedule(payload));
      if (createNotificationsSchedule.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  const updateTemplate = useCallback(
    async (payload: any) => {
      const result = await dispatch(updateNotificationsSchedule(payload));
      if (updateNotificationsSchedule.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  const removeTemplate = useCallback(
    async (id: number) => {
      const result = await dispatch(deleteNotificationsSchedule(id));
      if (deleteNotificationsSchedule.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  return {
    templates: data?.data || data || [],
    loading,
    error,
    refresh: () => dispatch(fetchNotificationsSchedule(params)),
    createTemplate,
    updateTemplate,
    removeTemplate,
  };
};

export default useNotificationsSchedule;
